import { useState, useCallback, useEffect } from 'react';
import { useFoldoutMenu } from '../../../hooks/useFoldoutMenu/useFoldoutMenu';
import { Searchfield } from '../Text/Searchfield';
import { ChipChevron } from '../ChipButton/icons/ChipChevron';
import {
  DropdownButtonStyled,
  FloatingContainer,
  Item,
  ItemsContainer,
  SearchWrapper,
  NoWrap,
  IconWrapper,
  ChevronWrapper,
} from './styles';

export type DropdownButtonProps = {
  items?: { label: string; value: string; icon?: React.ReactNode }[];
  onChange?: (selectedValue: string) => void;
  searchFieldPlaceholder?: string;
  searchLimit?: number;
  testId?: string;
  label?: string;
  icon?: React.ReactNode;
  rounded?: boolean;
  children?: React.ReactNode;
  enabled?: boolean;
  setIsOpen?: (func: (isOpen: boolean) => void) => void;
  containerMinWidth?: string;
};

export const DropdownButton = ({
  children,
  items,
  onChange,
  testId,
  label,
  icon,
  rounded,
  enabled = true,
  searchFieldPlaceholder,
  setIsOpen,
  searchLimit = 10,
  containerMinWidth,
}: DropdownButtonProps) => {
  const {
    refs,
    getReferenceProps,
    getFloatingProps,
    floatingStyles,
    setIsOpen: setOpen,
    isOpen,
  } = useFoldoutMenu({ placement: 'bottom-end' });

  useEffect(() => {
    setIsOpen?.(setOpen);
  }, [setOpen, setIsOpen]);

  const [itemsToShow, setItemsToShow] = useState(items);

  const filterItems = useCallback(
    (query: string) => {
      const filteredItems = items?.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase()),
      );

      setItemsToShow(filteredItems);
    },
    [items],
  );

  return (
    <>
      <DropdownButtonStyled
        disabled={!enabled}
        rounded={rounded}
        type="button"
        data-testid={testId || 'dropdown-button'}
        ref={(node) => {
          refs.setReference(node);
        }}
        {...getReferenceProps()}
        tabIndex={0}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(!isOpen);
        }}
      >
        {icon && <IconWrapper>{icon}</IconWrapper>}
        {label && <NoWrap>{label}</NoWrap>}
        {label && !icon && (
          <ChevronWrapper>
            <ChipChevron rotation={isOpen ? 180 : 0} />
          </ChevronWrapper>
        )}
      </DropdownButtonStyled>
      {isOpen && (
        <FloatingContainer
          ref={(node) => {
            refs.setFloating(node);
          }}
          {...getFloatingProps()}
          style={floatingStyles}
          containerMinWidth={containerMinWidth}
        >
          {children}
          {items && (
            <>
              {items.length > searchLimit && (
                <SearchWrapper>
                  <Searchfield
                    placeholder={searchFieldPlaceholder}
                    onChange={(query) => {
                      filterItems(query);
                    }}
                  />
                </SearchWrapper>
              )}
              <ItemsContainer>
                {itemsToShow &&
                  itemsToShow.map((item) => (
                    <Item
                      tabIndex={0}
                      key={item.value}
                      onClick={() => {
                        onChange?.(item.value);
                        setOpen(false);
                      }}
                    >
                      <span>{item.label}</span>
                      {item.icon && <span>{item.icon}</span>}
                    </Item>
                  ))}
              </ItemsContainer>
            </>
          )}
        </FloatingContainer>
      )}
    </>
  );
};
